import React from "react";
import { Typography } from "top-brains-ui";
import { LogOut } from "lucide-react";
import { isElectronApp } from "@src/utils/envUtils";
import { getExitingLoaderTheme } from "./exitingLoaderTheme";

interface ExitingLoaderProps {
  theme?: string;
  isExiting: boolean;
}

export const ExitingLoader: React.FC<ExitingLoaderProps> = ({
  theme = "app-theme",
  isExiting,
}) => {
  const isElectron = isElectronApp();
  const {
    exitBg,
    cardBg,
    cardBorder,
    cardShadow,
    accentColor,
    spinnerBorder,
    spinnerShadow,
    iconBg,
    iconBorder,
    titleColor,
    subtextColor,
  } = getExitingLoaderTheme(theme, isElectron);

  const isWindowExiting = typeof window !== "undefined" && !!(window as any).isExiting;

  if (!isExiting && !isWindowExiting) {
    return null;
  }

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "100vh",
      width: "100vw",
      background: exitBg,
      color: "white",
      fontFamily: "'Inter', 'Poppins', sans-serif",
      position: "fixed",
      top: 0,
      left: 0,
      zIndex: 9999999,
      overflow: "hidden"
    }}>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes pulse-exit {
          0%, 100% { opacity: 0.6; transform: scale(0.96); }
          50% { opacity: 1; transform: scale(1.04); }
        }
      `}</style>
      
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "40px 60px",
        background: cardBg,
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        borderRadius: "24px",
        border: cardBorder,
        boxShadow: cardShadow,
        maxWidth: "480px",
        width: "90%",
        textAlign: "center"
      }}>
        <div style={{ position: "relative", marginBottom: "32px", width: "90px", height: "90px" }}>
          <div style={{
            width: "100%",
            height: "100%",
            borderRadius: "50%",
            border: spinnerBorder,
            borderTopColor: accentColor,
            animation: "spin 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite",
            boxShadow: spinnerShadow
          }} />
          <div style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "48px",
            height: "48px",
            borderRadius: "50%",
            background: iconBg,
            border: iconBorder,
            animation: "pulse-exit 2s infinite ease-in-out"
          }}>
            <LogOut size={22} color={accentColor} style={{ marginLeft: "2px" }} />
          </div>
        </div>

        <Typography 
          fontSize={22} 
          fontWeight="semi-bold" 
          style={{ 
            marginBottom: "12px", 
            color: titleColor,
            letterSpacing: "-0.5px"
          }}
        >
          Exiting Application
        </Typography>
        
        <Typography 
          fontSize={14} 
          style={{ 
            color: subtextColor,
            lineHeight: "1.6",
            maxWidth: "340px"
          }}
        >
          Finalizing your session, saving progress, and closing the application safely. Please do not close the window.
        </Typography>
      </div>
    </div>
  );
};
